import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  useFetchEmployeesQuery,
  useDeleteEmployeeMutation,
  getFullEmployeePhotoUrl,
  type EmployeeRecord,
} from "../../store/api/employeesApi";
import "./EmployeeListPage.css";

const STATUS_COLORS: Record<string, { bg: string; color: string; dot: string }> = {
  active:   { bg: "#d1fae5", color: "#065f46", dot: "#10b981" },
  inactive: { bg: "#fee2e2", color: "#9f1239", dot: "#f43f5e" },
  pending:  { bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
};

const DEPT_COLORS = [
  "#6366f1", "#0ea5e9", "#10b981", "#f59e0b",
  "#f43f5e", "#8b5cf6", "#14b8a6", "#ec4899",
];

const deptColor = (dept: string) =>
  DEPT_COLORS[dept.charCodeAt(0) % DEPT_COLORS.length];

const initials = (first: string, last: string) =>
  `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();

const EmployeeCard: React.FC<{
  emp: EmployeeRecord;
  onDelete: (id: number) => void;
  deleting: boolean;
}> = ({ emp, onDelete, deleting }) => {
  const status   = (emp.status ?? "active").toLowerCase();
  const sc       = STATUS_COLORS[status] ?? STATUS_COLORS.active;
  const color    = deptColor(emp.department || "A");
  const bg       = `${color}18`;
  const photoSrc = getFullEmployeePhotoUrl(emp.profilePhotoUrl);

  return (
    <div className="emp-card">
      {/* Status dot */}
      <span className="emp-status-dot" style={{ background: sc.dot }} title={status} />

      {/* Avatar — photo if available, else coloured initials */}
      <div className="emp-avatar" style={photoSrc ? {} : { background: bg, color }}>
        {photoSrc
          ? <img src={photoSrc} alt={`${emp.firstName} ${emp.lastName}`} className="emp-avatar-img" />
          : initials(emp.firstName, emp.lastName)
        }
      </div>

      {/* Info */}
      <div className="emp-name">{emp.firstName} {emp.lastName}</div>
      <div className="emp-role">{emp.role || "—"}</div>
      <div className="emp-dept">{emp.department || "—"}</div>

      {/* Tags */}
      <div className="emp-tags">
        {emp.department && (
          <span className="emp-tag" style={{ background: bg, color }}>{emp.department}</span>
        )}
        {emp.role && (
          <span className="emp-tag emp-tag--neutral">{emp.role}</span>
        )}
      </div>

      {/* Status badge */}
      <div className="emp-status-badge" style={{ background: sc.bg, color: sc.color }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </div>

      {/* Action row */}
      <div className="emp-actions">
        {emp.email && (
          <a href={`mailto:${emp.email}`} className="emp-action-btn" title={emp.email}>
            <i className="bi bi-envelope" />
          </a>
        )}
        {emp.phone && (
          <a href={`tel:${emp.phone}`} className="emp-action-btn" title={emp.phone}>
            <i className="bi bi-telephone" />
          </a>
        )}
        <Link to={`/admin/employees/${emp.id}/view`} className="emp-action-btn" title="View details">
          <i className="bi bi-eye" />
        </Link>
        <Link to={`/admin/employees/${emp.id}/edit`} className="emp-action-btn" title="Edit">
          <i className="bi bi-pencil" />
        </Link>
        <button
          type="button"
          className="emp-action-btn emp-action-btn--danger"
          title="Delete"
          disabled={deleting}
          onClick={() => {
            if (window.confirm(`Delete ${emp.firstName} ${emp.lastName}?`)) {
              onDelete(emp.id);
            }
          }}
        >
          <i className="bi bi-trash3" />
        </button>
      </div>
    </div>
  );
};

const EmployeeListPage: React.FC = () => {
  const [search, setSearch]   = useState("");
  const [filter, setFilter]   = useState<"all" | "active" | "inactive">("all");
  const [view,   setView]     = useState<"grid" | "list">("grid");

  const { data: employees = [], isLoading, isError } = useFetchEmployeesQuery();
  const [deleteEmployee, { isLoading: isDeleting }]  = useDeleteEmployeeMutation();

  const filtered = employees.filter((e) => {
    const matchSearch =
      !search ||
      `${e.firstName} ${e.lastName} ${e.department} ${e.role} ${e.email}`
        .toLowerCase()
        .includes(search.toLowerCase());
    const matchFilter =
      filter === "all" || (e.status ?? "active").toLowerCase() === filter;
    return matchSearch && matchFilter;
  });

  const activeCount   = employees.filter((e) => (e.status ?? "active").toLowerCase() === "active").length;
  const inactiveCount = employees.filter((e) => (e.status ?? "active").toLowerCase() === "inactive").length;

  const handleDelete = async (id: number) => {
    try { await deleteEmployee(id).unwrap(); }
    catch { /* error handled globally */ }
  };

  return (
    <section className="admin-page emp-page">

      {/* ── Page header ─────────────────────────────── */}
      <div className="emp-page-header">
        <div>
          <h2 className="emp-page-title">Employees</h2>
          <p className="emp-page-sub">
            {employees.length} total · {activeCount} active · {inactiveCount} inactive
          </p>
        </div>
        <Link to="/admin/employees/new" className="emp-add-btn">
          <i className="bi bi-person-plus-fill" /> Add Employee
        </Link>
      </div>

      {/* ── Toolbar ─────────────────────────────────── */}
      <div className="emp-toolbar">
        <div className="emp-search-wrap">
          <i className="bi bi-search emp-search-icon" />
          <input
            type="search"
            className="emp-search-input"
            placeholder="Search name, role, department…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="emp-toolbar-right">
          {/* Filter chips */}
          <div className="emp-filter-chips">
            {(["all", "active", "inactive"] as const).map((f) => (
              <button
                key={f}
                type="button"
                className={`emp-chip${filter === f ? " emp-chip--active" : ""}`}
                onClick={() => setFilter(f)}
              >
                {f === "all" ? `All (${employees.length})` : f === "active" ? `Active (${activeCount})` : `Inactive (${inactiveCount})`}
              </button>
            ))}
          </div>

          {/* View toggle */}
          <div className="emp-view-toggle">
            <button
              type="button"
              className={`emp-view-btn${view === "grid" ? " active" : ""}`}
              onClick={() => setView("grid")}
              title="Grid view"
            >
              <i className="bi bi-grid-3x3-gap" />
            </button>
            <button
              type="button"
              className={`emp-view-btn${view === "list" ? " active" : ""}`}
              onClick={() => setView("list")}
              title="List view"
            >
              <i className="bi bi-list-ul" />
            </button>
          </div>
        </div>
      </div>

      {/* ── States ──────────────────────────────────── */}
      {isLoading && (
        <div className="emp-state-center">
          <span className="spinner-border text-secondary" role="status" />
          <span className="emp-state-text">Loading employees…</span>
        </div>
      )}

      {isError && !isLoading && (
        <div className="emp-state-center emp-state-error">
          <i className="bi bi-exclamation-triangle-fill" />
          <span>Failed to load employees. Check your connection and try again.</span>
        </div>
      )}

      {!isLoading && !isError && filtered.length === 0 && (
        <div className="emp-state-center emp-state-empty">
          <i className="bi bi-people" />
          <span>{search || filter !== "all" ? "No employees match your filter." : "No employees yet."}</span>
          {!search && filter === "all" && (
            <Link to="/admin/employees/new" className="emp-add-btn emp-add-btn--sm">
              <i className="bi bi-person-plus-fill" /> Add First Employee
            </Link>
          )}
        </div>
      )}

      {/* ── Grid view ───────────────────────────────── */}
      {!isLoading && !isError && filtered.length > 0 && view === "grid" && (
        <div className="emp-grid">
          {filtered.map((emp) => (
            <EmployeeCard
              key={emp.id}
              emp={emp}
              onDelete={handleDelete}
              deleting={isDeleting}
            />
          ))}
        </div>
      )}

      {/* ── List view ───────────────────────────────── */}
      {!isLoading && !isError && filtered.length > 0 && view === "list" && (
        <div className="emp-list-card">
          <table className="emp-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Role</th>
                <th>Status</th>
                <th>Contact</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((emp) => {
                const status    = (emp.status ?? "active").toLowerCase();
                const sc        = STATUS_COLORS[status] ?? STATUS_COLORS.active;
                const color     = deptColor(emp.department || "A");
                const bg        = `${color}18`;
                const photoSrc  = getFullEmployeePhotoUrl(emp.profilePhotoUrl);
                return (
                  <tr key={emp.id}>
                    <td>
                      <div className="emp-list-identity">
                        <div className="emp-avatar emp-avatar--sm" style={photoSrc ? {} : { background: bg, color }}>
                          {photoSrc
                            ? <img src={photoSrc} alt={`${emp.firstName} ${emp.lastName}`} className="emp-avatar-img" />
                            : initials(emp.firstName, emp.lastName)
                          }
                        </div>
                        <div>
                          <div className="emp-list-name">{emp.firstName} {emp.lastName}</div>
                          <div className="emp-list-email">{emp.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className="emp-tag" style={{ background: bg, color }}>{emp.department || "—"}</span></td>
                    <td><span className="emp-tag emp-tag--neutral">{emp.role || "—"}</span></td>
                    <td>
                      <span className="emp-status-badge" style={{ background: sc.bg, color: sc.color }}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <div className="emp-list-contact">
                        {emp.email && <a href={`mailto:${emp.email}`} className="emp-action-btn" title={emp.email}><i className="bi bi-envelope" /></a>}
                        {emp.phone && <a href={`tel:${emp.phone}`} className="emp-action-btn" title={emp.phone}><i className="bi bi-telephone" /></a>}
                      </div>
                    </td>
                    <td>
                      <div className="emp-list-contact">
                        <Link to={`/admin/employees/${emp.id}/view`} className="emp-action-btn" title="View details"><i className="bi bi-eye" /></Link>
                        <Link to={`/admin/employees/${emp.id}/edit`} className="emp-action-btn" title="Edit"><i className="bi bi-pencil" /></Link>
                        <button
                          type="button"
                          className="emp-action-btn emp-action-btn--danger"
                          disabled={isDeleting}
                          onClick={() => window.confirm(`Delete ${emp.firstName} ${emp.lastName}?`) && handleDelete(emp.id)}
                        >
                          <i className="bi bi-trash3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default EmployeeListPage;
